import React, { type MouseEventHandler, type PropsWithChildren } from "react";
import { observer } from "mobx-react";
import type { IResourcesStore } from "../../../../resources/ResourcesStore";
import type { IUploadStore } from "../../../UploadStore";
import { injectFromAppUpload } from "../../../inject";
import { UploadStatus } from "../../../FileUpload";
import type { IPatientToUpload } from "../../../PatientToUpload";
import { StaticNotificationComponent } from "../../../../shared-components/static-notification/StaticNotificationComponent";
import { humanizeFileSize } from "../../../../util/functions/humanizeFileSize";
import { assertUnreachable } from "../../../../util/assertions/assertUnreachable";
import { RichToolTipComponent } from "../../../../shared-components/rich-tooltip/RichToolTipComponent";
import type { IUploadCodeAndSourceSelectionStore } from "../../upload-code-and-source-selection/UploadCodeAndSourceSelectionStore";
import { DobButton } from "@dedalusdiit/dob-components";

interface IUploadButtonLogicComponentProps {
    resourcesStore?: IResourcesStore;
    uploadStore?: IUploadStore;
    patientToUpload: IPatientToUpload;
    uploadCodeAndSourceSelectionStore?: IUploadCodeAndSourceSelectionStore;
}

@injectFromAppUpload<IUploadButtonLogicComponentProps>(stores => ({
    uploadStore: stores.uploadStore,
    resourcesStore: stores.resourcesStore,
    uploadCodeAndSourceSelectionStore: stores.uploadCodeAndSourceSelectionStore,
}))
@observer
export class UploadButtonLogicComponent extends React.Component<PropsWithChildren<IUploadButtonLogicComponentProps>> {
    startQueueingUpload: MouseEventHandler<HTMLButtonElement> = event => {
        const { patientToUpload } = this.props;
        patientToUpload.startQueueingUpload();
    };

    pauseUpload: MouseEventHandler<HTMLButtonElement> = (event): void => {
        const { patientToUpload } = this.props;
        patientToUpload.pauseUpload();
    };

    cancelUpload: MouseEventHandler<HTMLButtonElement> = (event): void => {
        const { patientToUpload } = this.props;
        patientToUpload.cancelUpload();
    };

    resumeUpload: MouseEventHandler<HTMLButtonElement> = (event): void => {
        const { patientToUpload } = this.props;
        patientToUpload.resumeUpload();
    };

    retryUpload: MouseEventHandler<HTMLButtonElement> = (event): void => {
        const { patientToUpload } = this.props;
        patientToUpload.retryUpload();
    };

    resetFiles: MouseEventHandler<HTMLButtonElement> = (event): void => {
        const { uploadStore } = this.props;
        uploadStore!.resetFiles();
    };

    render() {
        const { resourcesStore, patientToUpload, uploadStore, uploadCodeAndSourceSelectionStore } = this.props;
        const { resources } = resourcesStore!;
        const { patientConsent, privacyPolicyConsent, readyToUpload, status, filesToUpload, filesToUploadTotalSize } = patientToUpload;
        const { allUploadsDone, reachedMaxUploadSize } = uploadStore!;
        const { selectedSourceUploadSizeLimitInGb } = uploadCodeAndSourceSelectionStore!;

        if (reachedMaxUploadSize) {
            return (
                <StaticNotificationComponent type={"error"} title={resources.UploadErrorExceedsSize}>
                    {resources.XFiles.replace("{0}", filesToUpload.length.toString())} ({humanizeFileSize(filesToUploadTotalSize)})
                    <br />
                    {resources.UploadErrorExceedsSizeInfo.replace("{0}", selectedSourceUploadSizeLimitInGb + "GB")}
                    {this.props.children}
                </StaticNotificationComponent>
            );
        }

        switch (status) {
            case UploadStatus.NotStarted:
                return (
                    <StaticNotificationComponent
                        type={"info"}
                        title={!patientConsent || !privacyPolicyConsent ? resources.AgreeToPrivacyAndPatientConsent : resources.ReadyToUpload}
                        hideIcon={true}
                    >
                        <RichToolTipComponent toolTipResource={!patientConsent || !privacyPolicyConsent ? "AgreeToPrivacyAndPatientConsentInfo" : undefined}>
                            <DobButton icon={"play-fill"} disabled={!readyToUpload} onClick={this.startQueueingUpload}>
                                {resources.StartUpload}
                            </DobButton>
                        </RichToolTipComponent>
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.Paused:
                return (
                    <StaticNotificationComponent type={"warning"} title={resources.UploadIsPaused} customClass={"paused"}>
                        <DobButton color={"neutral"} outline={true} icon={"play-fill"} onClick={this.resumeUpload}>
                            {resources.ResumeUpload}
                        </DobButton>
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.Started:
            case UploadStatus.Queued:
                return (
                    <StaticNotificationComponent type={"info"} title={resources.Uploading} customClass={"loading"}>
                        <DobButton color={"neutral"} outline={true} icon={"pause-fill"} onClick={this.pauseUpload}>
                            {resources.PauseUpload}
                        </DobButton>
                        <DobButton color={"neutral"} outline={true} icon={"x"} onClick={this.cancelUpload}>
                            {resources.CancelUpload}
                        </DobButton>
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.ValidatingFiles:
                return (
                    <StaticNotificationComponent type={"info"} title={resources.ValidatingFiles} customClass={"loading"}>
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.Done:
                return (
                    <StaticNotificationComponent type={"success"} title={resources.UploadSuccessful}>
                        {allUploadsDone && (
                            <DobButton color={"neutral"} outline={true} onClick={this.resetFiles}>
                                {resources.StartANewUpload}
                            </DobButton>
                        )}
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.MaliciousContent:
                return (
                    <StaticNotificationComponent type={"error"} title={resources.MaliciousContent}>
                        {resources.ImagesRemovedAndUploadIsCancelled}
                        <br />
                        <br />
                        <DobButton color={"neutral"} outline={true} onClick={this.resetFiles}>
                            {resources.StartANewUpload}
                        </DobButton>
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.NotADicomFile:
                return (
                    <StaticNotificationComponent type={"error"} title={resources.OnlyDicomFilesAllowed}>
                        {resources.ImagesRemovedAndUploadIsCancelled}
                        <br />
                        <br />
                        <DobButton color={"neutral"} outline={true} onClick={this.resetFiles}>
                            {resources.StartANewUpload}
                        </DobButton>
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.Failed:
                return (
                    <StaticNotificationComponent type={"error"} title={resources.SomethingWentWrong}>
                        {resources.SomethingWentWrongInfo}
                        <br />
                        <br />
                        <DobButton color={"neutral"} outline={true} icon={"arrow-clockwise"} onClick={this.retryUpload}>
                            {resources.RetryUpload}
                        </DobButton>
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.RateLimited:
                return (
                    <StaticNotificationComponent type={"warning"} title={resources.RateLimited}>
                        {resources.RateLimitedInfo}
                        <br />
                        {resources.PleaseTryAgainShortly}
                        <br />
                        {resources.SomethingWentWrongInfo}
                        <br />
                        <br />
                        <DobButton color={"neutral"} outline={true} icon={"arrow-clockwise"} onClick={this.retryUpload}>
                            {resources.RetryUpload}
                        </DobButton>
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.ExceededUploadSizeLimit:
                return (
                    <StaticNotificationComponent type={"error"} title={resources.ExceededUploadSizeLimit}>
                        {resources.ExceededUploadSizeLimitInfo.replace("{0}", selectedSourceUploadSizeLimitInGb + "GB")}
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.IpBlocked:
                return (
                    <StaticNotificationComponent type={"error"} title={resources.IpBlocked}>
                        {resources.IpBlockedInfo}
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.UploadCodeExpired:
                return (
                    <StaticNotificationComponent type={"error"} title={resources.UploadCodeExpired}>
                        {resources.UploadCodeExpiredInfo}
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            case UploadStatus.Cancelled:
                return (
                    <StaticNotificationComponent type={"error"} title={resources.UploadIsCancelled}>
                        <DobButton color={"neutral"} outline={true} onClick={this.resetFiles}>
                            {resources.StartANewUpload}
                        </DobButton>
                        {this.props.children}
                    </StaticNotificationComponent>
                );
            default:
                //Should never happen
                assertUnreachable(status);
                return null;
        }
    }
}
